import { QueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/api/keys';
import { contactsApi } from '@/api/services/contactsApi';
import { groupManagementApi } from '@/api/services/groupManagementApi';
import { groupsApi } from '@/api/services/groupsApi';
import {
  pendingReactionsAtom,
  addPendingReactionAtom,
  removePendingReactionByQueueIdAtom,
} from '@/atoms/pendingReactionsAtom';
import { jotaiStore } from '@/providers/jotaiStore';
import { DMMessage, DMConversation, MessageReaction } from '@/types/dm';
import { Message as GroupMessage } from '@/types/groups';
import {
  WebSocketV2Message,
  MessageQueuedData,
  MessageStatusData,
  MessageFailedData,
  MessageCompletedData,
  MessageCreatedData,
  MessageDeletedData,
  MessageEditedData,
  ReactionAddedData,
  ReactionRemovedData,
  ConversationUpdatedData,
  ThreadsReadyData,
  SystemSyncData,
  SessionStatusData,
  SessionDeletionQueuedData,
  SessionDeletionProgressData,
  SessionDeletionCompleteData,
  SessionDeletionFailedData,
  MessagesInfiniteData,
  GroupMessagesInfiniteData,
  AnyMessage,
  BackendMessageResponse,
} from '@/types/websocket';

class MessageCache {
  private cache = new Map<string, AnyMessage>();
  private maxSize = 1000;

  set(messageId: string, message: AnyMessage) {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) this.cache.delete(firstKey);
    }
    this.cache.set(messageId, message);
  }

  get(messageId: string): AnyMessage | undefined {
    return this.cache.get(messageId);
  }

  has(messageId: string): boolean {
    return this.cache.has(messageId);
  }

  delete(messageId: string) {
    this.cache.delete(messageId);
  }

  clear() {
    this.cache.clear();
  }
}

export class WebSocketService {
  private static instance: WebSocketService | undefined;
  private ws: WebSocket | null = null;
  private queryClient: QueryClient | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private connectionCheckInterval: ReturnType<typeof setInterval> | null = null;
  private lastMessageTime = Date.now();
  private isIntentionallyClosed = false;

  private lastSeq = 0;
  private messageCache = new MessageCache();
  private pendingAcks = new Set<number>();
  private optimisticMessages = new Map<string, Partial<AnyMessage>>();
  private jotaiStore = jotaiStore;
  private reactionQueueMap = new Map<
    string,
    { conversationId: string; conversationType: 'dm' | 'group' }
  >();

  // Connection status listeners
  private connectionListeners = new Set<(isConnected: boolean) => void>();

  // Sync progress listeners
  private syncProgressListeners = new Set<
    (progress: {
      currentStep: 'groups' | 'contacts' | 'groupData';
      completedSteps: ('groups' | 'contacts' | 'groupData')[];
      totalSteps: number;
      isComplete: boolean;
    }) => void
  >();

  // Track if sync is in progress (prevents duplicate syncs)
  private isSyncing = false;

  private constructor() {
    this.setupConnectionCheck();
    const storedSeq = localStorage.getItem('ws_v2_last_seq');
    if (storedSeq) {
      this.lastSeq = parseInt(storedSeq, 10);
    }
  }

  public static getInstance(): WebSocketService {
    WebSocketService.instance ??= new WebSocketService();
    return WebSocketService.instance;
  }

  public init(queryClient: QueryClient) {
    if (this.queryClient === queryClient) {
      return;
    }

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.queryClient = queryClient;
    this.connect();
  }

  private connect() {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const apiUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
      const host = apiUrl?.replace(/^https?:\/\//, '') ?? window.location.host;
      const wsUrl = `${protocol}//${host}/ws`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.startHeartbeat();
        this.notifyConnectionListeners(true);

        if (this.lastSeq > 0) {
          this.sendMessage({
            type: 'client.sync_request',
            lastSeq: this.lastSeq,
          });
        }
      };

      this.ws.onmessage = async (event) => {
        this.lastMessageTime = Date.now();
        try {
          const data = JSON.parse(event.data as string) as unknown;

          if (data && typeof data === 'object' && 'seq' in data) {
            await this.handleV2Message(data as WebSocketV2Message);
          }
        } catch {
          // Silently ignore parse errors to prevent disruption
        }
      };

      this.ws.onerror = () => {
        // Error handling is done in onclose
      };

      this.ws.onclose = () => {
        this.stopHeartbeat();
        this.notifyConnectionListeners(false);

        if (!this.isIntentionallyClosed) {
          this.scheduleReconnect();
        }
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, this.reconnectDelay);

    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private setupConnectionCheck() {
    this.connectionCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastMessage = now - this.lastMessageTime;

      if (
        timeSinceLastMessage > 60000 &&
        this.ws?.readyState === WebSocket.OPEN
      ) {
        this.ws.close();
        this.connect();
      }
    }, 10000);
  }

  private sendMessage(message: Record<string, unknown>) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private sendAck(seq: number) {
    this.sendMessage({
      type: 'client.ack',
      seq,
    });
    this.pendingAcks.delete(seq);
  }

  private async handleV2Message(message: WebSocketV2Message) {
    if (message.seq > this.lastSeq) {
      this.lastSeq = message.seq;
      localStorage.setItem('ws_v2_last_seq', String(message.seq));
    }

    if (message.requiresAck) {
      this.sendAck(message.seq);
    }

    switch (message.type) {
      case 'message.queued':
        this.handleMessageQueued(message.data as MessageQueuedData);
        break;
      case 'message.status':
        this.handleMessageStatus(message.data as MessageStatusData);
        break;
      case 'message.completed':
        this.handleMessageCompleted(message.data as MessageCompletedData);
        break;
      case 'message.created':
        await this.handleMessageCreated(message.data as MessageCreatedData);
        break;
      case 'message.failed':
        this.handleMessageFailed(message.data as MessageFailedData);
        break;
      case 'message.deleted':
        this.handleMessageDeleted(message.data as MessageDeletedData);
        break;
      case 'message.edited':
        this.handleMessageEdited(message.data as MessageEditedData);
        break;
      case 'reaction.added':
        this.handleReactionAdded(message.data as ReactionAddedData);
        break;
      case 'reaction.removed':
        this.handleReactionRemoved(message.data as ReactionRemovedData);
        break;
      case 'conversation.updated':
        this.handleConversationUpdated(message.data as ConversationUpdatedData);
        break;
      case 'threads.ready':
        this.handleThreadsReady(message.data as ThreadsReadyData);
        break;
      case 'system.sync':
        await this.handleSystemSync(message.data as SystemSyncData);
        break;
      case 'session.status':
        this.handleSessionStatus(message.data as SessionStatusData);
        break;
      case 'session.deletion.queued':
        this.handleSessionDeletionQueued(
          message.data as SessionDeletionQueuedData,
        );
        break;
      case 'session.deletion.progress':
        this.handleSessionDeletionProgress(
          message.data as SessionDeletionProgressData,
        );
        break;
      case 'session.deletion.complete':
        this.handleSessionDeletionComplete(
          message.data as SessionDeletionCompleteData,
        );
        break;
      case 'session.deletion.failed':
        this.handleSessionDeletionFailed(
          message.data as SessionDeletionFailedData,
        );
        break;
      default:
        break;
    }
  }

  private handleMessageQueued(data: MessageQueuedData) {
    if (!this.queryClient) return;

    if (data.messageType === 'reaction') {
      if (data.replyTo) {
        console.log('[WebSocket] 📬 message.queued received for reaction:', {
          queueId: data.messageId,
          replyTo: data.replyTo,
          emoji: data.body || '(empty - removal)',
        });

        // Store mapping of queueId to conversation info for later use
        this.reactionQueueMap.set(data.messageId, {
          conversationId: data.conversationId,
          conversationType: data.conversationType,
        });

        // Check if this pending reaction already exists (frontend added it optimistically)
        const currentPending = this.jotaiStore.get(pendingReactionsAtom);

        // Look for existing entry with this queueId
        let alreadyExists = false;
        for (const [, reaction] of currentPending.entries()) {
          if (reaction.queueId === data.messageId) {
            alreadyExists = true;
            console.log(
              '[WebSocket] ⏭️ Pending reaction already exists, skipping duplicate',
            );
            break;
          }
        }

        // Only add if it doesn't exist (e.g., after page refresh/reconnect)
        if (!alreadyExists) {
          // Backend should be sending UUID in data.replyTo
          // Just use it directly - no complex lookup needed
          this.jotaiStore.set(addPendingReactionAtom, {
            targetMessageId: data.replyTo,
            queueId: data.messageId,
            emoji: data.body,
            conversationId: data.conversationId,
            conversationType: data.conversationType,
          });
        }
      }
      return;
    }

    if (data.conversationType === 'group') {
      this.handleGroupMessageQueued(data);
    } else {
      this.handleDMMessageQueued(data);
    }
  }

  private handleDMMessageQueued(data: MessageQueuedData) {
    if (!this.queryClient) return;

    // Update conversation list with the queued message
    console.log(
      '[WebSocket] 📝 Updating conversation list for queued message:',
      data.conversationId,
    );
    this.updateConversationMetadata(data.conversationId, {
      lastMessage: data.body || '[Media]',
      lastMessageTime: new Date(data.timestamp).toISOString(),
      unreadCount: 0, // Queued messages are from me, so no unread
    });

    const isMediaMessage =
      data.hasMedia ||
      ['image', 'video', 'audio', 'document'].includes(data.messageType ?? '');

    let quotedMessage = data.quotedMessage;
    if (!quotedMessage && data.replyTo) {
      const messages = this.queryClient.getQueriesData<{
        pages: { messages: DMMessage[] }[];
      }>({
        queryKey: ['dm-messages', 'infinite', data.conversationId],
      });

      if (messages.length > 0 && messages[0][1]) {
        const allMessages = messages[0][1].pages.flatMap(
          (page) => page.messages,
        );

        const foundMessage = allMessages.find(
          (msg) =>
            msg.wahaMessageId === data.replyTo || msg.id === data.replyTo,
        );

        if (foundMessage) {
          const quotedSenderName = foundMessage.fromMe
            ? 'You'
            : (foundMessage.senderDisplayName ?? 'Unknown');

          quotedMessage = {
            id: foundMessage.id,
            body: foundMessage.body ?? null,
            senderDisplayName: quotedSenderName,
            senderId: foundMessage.senderId,
            messageType: foundMessage.messageType,
            waTimestamp: foundMessage.waTimestamp,
            fromMe: foundMessage.fromMe,
            media: foundMessage.media.map((m) => ({
              id: `media-${m.id}`,
              mediaType: m.mediaType,
              originalFilename: m.originalFilename,
              presignedUrl: m.presignedUrl,
              mimeType: m.mimeType,
              sizeBytes: m.sizeBytes,
              uploadStatus: m.uploadStatus,
            })),
          };
        }
      }
    }

    const optimisticMessage: DMMessage = {
      id: data.messageId,
      status: data.status,
      waTimestamp: new Date(data.timestamp).toISOString(),
      body: data.body,
      fromMe: true, // This is a message we're sending, so it should always be fromMe

      senderId: undefined,
      senderDisplayName: 'You', // Always "You" for sent messages
      messageType: data.messageType ?? (isMediaMessage ? 'media' : 'text'),
      media: isMediaMessage
        ? [
            {
              id: `uploading-${data.messageId}`,
              s3Key: '',
              mediaType:
                data.messageType === 'image'
                  ? 'image'
                  : data.messageType === 'video'
                    ? 'video'
                    : data.messageType === 'audio'
                      ? 'audio'
                      : 'document',
              originalFilename: 'Uploading...',
              mimeType: '',
              sizeBytes: 0,
              uploadStatus: 'uploading',
              aiDescription: null,
              presignedUrl: null,
            },
          ]
        : [],
      quotedMessageId: data.quotedMessageId ?? data.replyTo ?? null,
      quotedMessage: quotedMessage
        ? {
            ...quotedMessage,
            media: quotedMessage.media?.map((m) => ({
              ...m,
              uploadStatus: m.presignedUrl ? 'completed' : 'uploading',
            })),
          }
        : undefined,
      type: 'queued' as const, // Mark as queued message
    } as DMMessage;

    this.optimisticMessages.set(data.messageId, optimisticMessage);
    this.addDMMessageToCache(data.conversationId, optimisticMessage);
  }

  private handleGroupMessageQueued(data: MessageQueuedData) {
    if (!this.queryClient) return;

    const isMediaMessage =
      data.hasMedia ||
      ['image', 'video', 'audio', 'document'].includes(data.messageType ?? '');

    let quotedMessage = data.quotedMessage;
    if (!quotedMessage && data.replyTo) {
      const messages =
        this.queryClient.getQueriesData<GroupMessagesInfiniteData>({
          queryKey: ['group-messages', 'infinite', data.conversationId],
        });

      if (messages.length > 0 && messages[0][1]) {
        const allMessages = messages[0][1].pages.flatMap(
          (page) => page.messages,
        );

        const foundMessage = allMessages.find(
          (msg) =>
            msg.wahaMessageId === data.replyTo || msg.id === data.replyTo,
        );

        if (foundMessage) {
          const quotedSenderName = foundMessage.fromMe
            ? 'You'
            : (foundMessage.senderDisplayName ?? 'Unknown');

          quotedMessage = {
            id: foundMessage.id,
            body: foundMessage.body ?? null,
            senderDisplayName: quotedSenderName,
            senderId: foundMessage.senderId,
            messageType: foundMessage.messageType,
            waTimestamp: foundMessage.waTimestamp,
            fromMe: foundMessage.fromMe,
            media: foundMessage.media
              ? foundMessage.media.map((m) => ({
                  id: `media-${m.id}`,
                  mediaType: m.mediaType as
                    | 'image'
                    | 'video'
                    | 'audio'
                    | 'document'
                    | 'sticker',
                  originalFilename: m.originalFilename,
                  presignedUrl: m.presignedUrl ?? null,
                  mimeType: m.mimeType,
                  sizeBytes: m.sizeBytes,
                  uploadStatus: m.uploadStatus,
                }))
              : [],
          };
        }
      }
    }

    const optimisticMessage: GroupMessage = {
      id: data.messageId,
      wahaMessageId: null,
      threadId: null,
      senderId: undefined,
      senderDisplayName: 'You',
      waTimestamp: new Date(data.timestamp).toISOString(),
      messageType: data.messageType ?? (isMediaMessage ? 'media' : 'text'),
      body: data.body,
      quotedMessageId: data.quotedMessageId ?? data.replyTo ?? null,
      quotedMessage: quotedMessage
        ? {
            ...quotedMessage,
            media: quotedMessage.media?.map((m) => ({
              id: `media-${Date.now().toString()}`,
              s3Key: null,
              mediaType: m.mediaType as string,
              originalFilename: m.originalFilename,
              mimeType: null,
              sizeBytes: null,
              uploadStatus: m.presignedUrl ? 'completed' : 'uploading',
              aiDescription: null,
              presignedUrl: m.presignedUrl,
            })),
          }
        : undefined,
      status: data.status,
      threadName: null,
      fromMe: true,
      lastEditedAt: null,
      editCount: null,
      editHistory: null,
      createdAt: new Date(data.timestamp).toISOString(),
      media: isMediaMessage
        ? [
            {
              id: `uploading-${data.messageId}`,
              s3Key: null,
              mediaType:
                data.messageType === 'image'
                  ? 'image'
                  : data.messageType === 'video'
                    ? 'video'
                    : data.messageType === 'audio'
                      ? 'audio'
                      : 'document',
              originalFilename: 'Uploading...',
              mimeType: null,
              sizeBytes: null,
              uploadStatus: 'uploading',
              aiDescription: null,
              tags: null,
              presignedUrl: null,
            },
          ]
        : undefined,
      type: 'queued' as const,
      reactions: {},
    } as GroupMessage;

    this.optimisticMessages.set(data.messageId, optimisticMessage);

    this.addGroupMessageToCache(data.conversationId, optimisticMessage);
  }

  private handleMessageStatus(data: MessageStatusData) {
    if (!this.queryClient) return;

    if (
      data.status === 'processing' &&
      !this.optimisticMessages.has(data.messageId)
    ) {
      return;
    }

    this.queryClient.setQueriesData(
      { queryKey: ['dm-messages', 'infinite'] },
      (oldData: MessagesInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === data.messageId ||
              msg.serverMessageId === data.messageId ||
              msg.wahaMessageId === data.messageId
                ? { ...msg, status: data.status }
                : msg,
            ),
          })),
        };
      },
    );

    this.queryClient.setQueriesData(
      { queryKey: ['group-messages', 'infinite'] },
      (oldData: GroupMessagesInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === data.messageId ||
              msg.serverMessageId === data.messageId ||
              msg.wahaMessageId === data.messageId
                ? { ...msg, status: data.status }
                : msg,
            ),
          })),
        };
      },
    );
  }

  private handleMessageCompleted(data: MessageCompletedData) {
    if (!this.queryClient) return;

    console.log('Hnalde msg trigger');

    this.optimisticMessages.delete(data.queueId);
    this.queryClient.setQueriesData(
      { queryKey: ['dm-messages', 'infinite'] },
      (oldData: MessagesInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === data.queueId && !msg.wahaMessageId
                ? {
                    ...msg, // Keep all existing message data including quotedMessage
                    wahaMessageId: data.wahaMessageId,
                    status: 'sent',
                    // Don't override fromMe, quotedMessage, etc - they're already in msg
                  }
                : msg,
            ),
          })),
        };
      },
    );

    this.queryClient.setQueriesData(
      { queryKey: ['group-messages', 'infinite'] },
      (oldData: GroupMessagesInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === data.queueId && !msg.wahaMessageId
                ? {
                    ...msg, // Keep all existing message data including quotedMessage
                    wahaMessageId: data.wahaMessageId,
                    status: 'sent',
                  }
                : msg,
            ),
          })),
        };
      },
    );
  }

  private handleMessageFailed(data: MessageFailedData) {
    if (!this.queryClient) return;

    // Update message status to failed in DM conversations
    if (data.conversationType === 'dm') {
      this.queryClient.setQueriesData(
        { queryKey: ['dm-messages', 'infinite'] },
        (oldData: MessagesInfiniteData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) =>
                msg.id === data.messageId ||
                msg.serverMessageId === data.messageId
                  ? {
                      ...msg,
                      status: 'failed',
                      errorMessage: data.errorMessage,
                    }
                  : msg,
              ),
            })),
          };
        },
      );
    }

    if (data.conversationType === 'group') {
      // Update message status to failed in group conversations
      this.queryClient.setQueriesData(
        { queryKey: ['group-messages', 'infinite'] },
        (oldData: GroupMessagesInfiniteData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) =>
                msg.id === data.messageId ||
                msg.serverMessageId === data.messageId
                  ? {
                      ...msg,
                      status: 'failed',
                      errorMessage: data.errorMessage,
                    }
                  : msg,
              ),
            })),
          };
        },
      );
    }

    // Clear from optimistic messages if present
    this.optimisticMessages.delete(data.messageId);
  }

  private async handleMessageCreated(data: MessageCreatedData) {
    if (!this.queryClient) return;

    // 1. Update conversation list IMMEDIATELY with metadata (if provided)
    if (data.conversation && data.conversationType === 'dm') {
      this.updateConversationMetadata(data.conversationId, data.conversation);
    } else if (data.conversation && data.conversationType === 'group') {
      this.updateGroupMetadata(data.conversationId, data.conversation);
    }

    // 2. Handle message based on conversation type
    if (data.conversationType === 'group') {
      await this.handleGroupMessageCreated(data);
    } else {
      await this.handleDMMessageCreated(data);
    }
  }

  private async handleDMMessageCreated(data: MessageCreatedData) {
    if (!this.queryClient) return;

    // If fromMe is true and source is 'app', treat it like a received message
    if (data.fromMe && data.source === 'app') {
      const isOpen = this.isConversationOpen(data.conversationId, 'dm');

      if (isOpen) {
        const message = await this.fetchDMMessage(data.messageId);
        if (message) {
          this.addDMMessageToCache(data.conversationId, message);

          // Invalidate media queries if message has media
          if (message.media.length > 0) {
            void this.queryClient.invalidateQueries({
              queryKey: ['media'],
              exact: false,
            });
          }
        }
      }
    } else if (data.fromMe && data.queueId) {
      // For sent messages (source not 'app') - update the optimistic message
      const message = await this.fetchDMMessage(data.messageId);
      if (!message) {
        return;
      }

      // Update the optimistic message with real data
      this.updateDMMessageInCache(data.conversationId, data.queueId, () => ({
        ...message,
        id: message.id, // Use real backend ID as primary ID
        serverMessageId: message.id, // Also store in serverMessageId for backward compatibility
      }));
    } else if (!data.fromMe) {
      const isOpen = this.isConversationOpen(data.conversationId, 'dm');

      if (isOpen) {
        const message = await this.fetchDMMessage(data.messageId);
        if (message) {
          this.addDMMessageToCache(data.conversationId, message);

          // Invalidate media queries if message has media
          if (message.media.length > 0) {
            void this.queryClient.invalidateQueries({
              queryKey: ['media'],
              exact: false,
            });
          }
        }
      }
    }
  }

  private async handleGroupMessageCreated(data: MessageCreatedData) {
    if (!this.queryClient) return;

    // Use conversationId directly (backend sends UUID)
    const groupId = data.conversationId;

    // If fromMe is true and source is 'app', treat it like a received message
    if (data.fromMe && data.source === 'app') {
      const isOpen = this.isConversationOpen(groupId, 'group');

      if (isOpen) {
        const message = await this.fetchGroupMessage(data.messageId);
        if (message) {
          this.addGroupMessageToCache(groupId, message);

          // Invalidate media queries if message has media
          if (message.media && message.media.length > 0) {
            void this.queryClient.invalidateQueries({
              queryKey: ['media'],
              exact: false,
            });
          }
        }
      }
    } else if (data.fromMe && data.queueId) {
      // For sent messages (source not 'app') - update the optimistic message
      const message = await this.fetchGroupMessage(data.messageId);
      if (!message) {
        return;
      }

      // Update the optimistic message with real data
      // Keep uploading status for media until we have the presigned URL
      this.updateGroupMessageInCache(groupId, data.queueId, () => ({
        ...message,
        id: message.id, // Use real backend ID as primary ID
        serverMessageId: message.id, // Also store in serverMessageId for backward compatibility
        // Preserve uploading state for media without presigned URLs
        media: message.media?.map((m) => ({
          ...m,
          uploadStatus: m.presignedUrl ? 'completed' : 'uploading',
        })),
      }));
    } else if (!data.fromMe) {
      const isOpen = this.isConversationOpen(groupId, 'group');

      if (isOpen) {
        const message = await this.fetchGroupMessage(data.messageId);
        if (message) {
          this.addGroupMessageToCache(groupId, message);

          // Invalidate media queries if message has media
          if (message.media && message.media.length > 0) {
            void this.queryClient.invalidateQueries({
              queryKey: ['media'],
              exact: false,
            });
          }
        }
      }
    }
  }

  private handleMessageDeleted(data: MessageDeletedData) {
    if (!this.queryClient) return;

    // Remove from cache
    this.messageCache.delete(data.messageId);

    const conversationId = data.conversationId;
    const limit = 20;

    // Determine which cache to update based on conversation type
    if (data.conversationType === 'dm') {
      // Remove from DM messages query
      this.queryClient.setQueriesData(
        { queryKey: ['dm-messages', 'infinite', conversationId, limit] },
        (oldData: MessagesInfiniteData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.filter(
                (msg) =>
                  msg.id !== data.messageId &&
                  msg.serverMessageId !== data.messageId &&
                  msg.wahaMessageId !== data.messageId,
              ),
            })),
          };
        },
      );
    } else {
      // Remove from group messages query
      this.queryClient.setQueriesData(
        { queryKey: ['group-messages', 'infinite', conversationId, limit] },
        (oldData: GroupMessagesInfiniteData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.filter(
                (msg) =>
                  msg.id !== data.messageId &&
                  msg.serverMessageId !== data.messageId &&
                  msg.wahaMessageId !== data.messageId,
              ),
            })),
          };
        },
      );
    }
  }

  private handleMessageEdited(data: MessageEditedData) {
    if (!this.queryClient) return;

    // Update message in cache
    const cachedMessage = this.messageCache.get(data.messageId);
    if (cachedMessage) {
      cachedMessage.body = data.newBody;
      this.messageCache.set(data.messageId, cachedMessage);
    }

    const conversationId = data.conversationId;
    const limit = 20;

    // Update DM messages
    if (data.conversationType === 'dm') {
      this.queryClient.setQueriesData(
        { queryKey: ['dm-messages', 'infinite', conversationId, limit] },
        (oldData: MessagesInfiniteData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) => {
                if (
                  msg.id === data.messageId ||
                  msg.serverMessageId === data.messageId ||
                  msg.wahaMessageId === data.messageId
                ) {
                  return {
                    ...msg,
                    body: data.newBody,
                  };
                }
                return msg;
              }),
            })),
          };
        },
      );

      // Force re-render for DM messages
      void this.queryClient.invalidateQueries({
        queryKey: ['dm-messages', 'infinite', conversationId],
        exact: false,
        refetchType: 'none',
      });
    } else {
      // Update group messages
      this.queryClient.setQueriesData(
        { queryKey: ['group-messages', 'infinite', conversationId, limit] },
        (oldData: GroupMessagesInfiniteData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) => {
                if (
                  msg.id === data.messageId ||
                  msg.serverMessageId === data.messageId ||
                  msg.wahaMessageId === data.messageId
                ) {
                  return {
                    ...msg,
                    body: data.newBody,
                  };
                }
                return msg;
              }),
            })),
          };
        },
      );

      // Force re-render for group messages
      void this.queryClient.invalidateQueries({
        queryKey: ['group-messages', 'infinite', conversationId],
        exact: false,
        refetchType: 'none',
      });
    }
  }

  private handleReactionAdded(data: ReactionAddedData) {
    if (!this.queryClient) return;

    console.log('[WebSocket] 🎉 reaction.added received:', {
      messageId: data.messageId,
      userId: data.userId,
      emoji: data.emoji,
      queueId: data.queueId,
      conversationId: data.conversationId,
      conversationType: data.conversationType,
      bustCache: data.bustCache,
    });

    // Clear queued reaction from Jotai store ONLY by queueId
    // Don't clear by targetMessageId as that could remove a different pending reaction
    if (data.queueId) {
      console.log(
        '[WebSocket] 🧹 Removing pending reaction by queueId:',
        data.queueId,
      );
      this.jotaiStore.set(removePendingReactionByQueueIdAtom, data.queueId);
    } else {
      console.warn(
        '[WebSocket] ⚠️ No queueId in reaction.added event - cannot remove pending state optimistically',
      );
      console.log('[WebSocket] 💡 Will rely on bustCache fetch to update UI');
    }

    // Get conversation info from our stored map or from data
    let conversationId = data.conversationId;
    let conversationType = data.conversationType;

    if (!conversationId && data.queueId) {
      const storedInfo = this.reactionQueueMap.get(data.queueId);
      if (storedInfo) {
        conversationId = storedInfo.conversationId;
        conversationType = storedInfo.conversationType;
        // Clean up the map
        this.reactionQueueMap.delete(data.queueId);
      }
    }

    if (!conversationId) {
      console.error('[WebSocket] No conversation info for reaction:', data);
      return;
    }

    // Determine query key based on conversation type
    const queryKey =
      conversationType === 'dm'
        ? ['dm-messages', 'infinite', conversationId]
        : ['group-messages', 'infinite', conversationId];

    // If bustCache is true, fetch the updated message from backend
    if (data.bustCache && data.messageId) {
      void this.fetchMessage(data.messageId, true).then((updatedMessage) => {
        if (updatedMessage && this.queryClient) {
          // Update the message in cache with the fetched data
          this.queryClient.setQueriesData(
            { queryKey },
            (oldData: MessagesInfiniteData | undefined) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  messages: page.messages
                    .map((msg) => {
                      const isTargetMsg =
                        msg.id === data.messageId ||
                        msg.serverMessageId === data.messageId ||
                        msg.wahaMessageId === data.messageId;

                      if (isTargetMsg) {
                        return {
                          ...msg,
                          reactions: updatedMessage.reactions ?? {},
                          queuedReaction: undefined,
                        };
                      }
                      return msg;
                    })
                    .filter(Boolean),
                })),
              };
            },
          );
        }
      });
    } else {
      // Fallback to local update if bustCache is not set
      this.queryClient.setQueriesData(
        { queryKey },
        (
          oldData: MessagesInfiniteData | GroupMessagesInfiniteData | undefined,
        ) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) => {
                // Handle partial message ID matching for local update
                const isTargetMsg =
                  msg.id === data.messageId ||
                  msg.serverMessageId === data.messageId ||
                  msg.wahaMessageId === data.messageId;

                if (isTargetMsg) {
                  const reactions: Record<string, MessageReaction> = {
                    ...msg.reactions,
                  };

                  if (data.emoji) {
                    // Add reaction with proper fields
                    reactions[data.userId] = {
                      emoji: data.emoji,
                      timestamp: new Date().toISOString(),
                      isFromMe: true,
                      senderDisplayName: 'You',
                    };
                  } else {
                    // Remove reaction if emoji is empty
                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                    delete reactions[data.userId];
                  }

                  return {
                    ...msg,
                    reactions,
                    queuedReaction: undefined, // Clear any queued reaction
                  };
                }
                return msg;
              }),
            })),
          };
        },
      );
    }
  }

  private handleReactionRemoved(data: ReactionRemovedData) {
    if (!this.queryClient) return;

    console.log('[WebSocket] 💥 reaction.removed received:', {
      messageId: data.messageId,
      userId: data.userId,
      emoji: data.emoji,
      queueId: data.queueId,
      conversationId: data.conversationId,
      conversationType: data.conversationType,
      bustCache: data.bustCache,
    });

    // Clear queued reaction from Jotai store ONLY by queueId
    // Don't clear by targetMessageId as that could remove a different pending reaction
    if (data.queueId) {
      console.log(
        '[WebSocket] 🧹 Removing pending reaction by queueId:',
        data.queueId,
      );
      this.jotaiStore.set(removePendingReactionByQueueIdAtom, data.queueId);
    } else {
      console.warn('[WebSocket] ⚠️ No queueId in reaction.removed event');
    }

    // Get conversation info from our stored map or from data
    let conversationId = data.conversationId;
    let conversationType = data.conversationType;

    if (!conversationId && data.queueId) {
      const storedInfo = this.reactionQueueMap.get(data.queueId);
      if (storedInfo) {
        conversationId = storedInfo.conversationId;
        conversationType = storedInfo.conversationType;
        // Clean up the map
        this.reactionQueueMap.delete(data.queueId);
      }
    }

    if (!conversationId) {
      console.error(
        '[WebSocket] No conversation info for reaction removal:',
        data,
      );
      return;
    }

    // Determine query key based on conversation type
    const queryKey =
      conversationType === 'dm'
        ? ['dm-messages', 'infinite', conversationId]
        : ['group-messages', 'infinite', conversationId];

    // Remove reaction from cache
    this.queryClient.setQueriesData(
      { queryKey },
      (oldData: MessagesInfiniteData | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) => {
              // Handle message ID matching for reaction removal
              const isTargetMsg =
                msg.id === data.messageId ||
                msg.serverMessageId === data.messageId ||
                msg.wahaMessageId === data.messageId;

              if (isTargetMsg) {
                const reactions = { ...(msg.reactions ?? {}) };
                // Remove user's reaction
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete reactions[data.userId];
                return { ...msg, reactions };
              }
              return msg;
            }),
          })),
        };
      },
    );
  }

  private handleConversationUpdated(data: ConversationUpdatedData) {
    if (!this.queryClient) return;

    // Update conversation metadata immediately
    this.updateConversationMetadata(data.conversationId, {
      lastMessage: data.lastMessage ?? '',
      lastMessageTime: data.lastMessageTime ?? new Date().toISOString(),
      unreadCount: data.unreadCount ?? 0,
    });
  }

  private handleThreadsReady(data: ThreadsReadyData) {
    if (!this.queryClient) return;

    // Invalidate thread queries for the conversation
    // This will trigger React Query to refetch the thread data
    if (data.conversationType === 'dm') {
      // Invalidate DM thread queries
      void this.queryClient.invalidateQueries({
        queryKey: ['dm-threads', data.conversationId],
      });
    } else {
      // Invalidate group thread queries
      void this.queryClient.invalidateQueries({
        queryKey: ['group-threads', data.conversationId],
      });
    }

    // Also invalidate any message threads that might be cached
    void this.queryClient.invalidateQueries({
      queryKey: ['message-threads', data.conversationId],
    });
  }

  private updateConversationMetadata(
    conversationId: string,
    metadata: {
      lastMessage: string;
      lastMessageTime: string;
      unreadCount: number;
    },
  ) {
    if (!this.queryClient) return;

    interface ConversationsData {
      pages: {
        conversations: DMConversation[];
        [key: string]: unknown;
      }[];
      [key: string]: unknown;
    }

    const queries = this.queryClient.getQueriesData<ConversationsData>({
      queryKey: ['dm-conversations'],
      exact: false,
    });

    // Check if conversation exists in any query
    const conversationExists = queries.some(([, data]) => {
      if (!data?.pages) return false;
      return data.pages.some((page) =>
        page.conversations.some((conv) => conv.contact.id === conversationId),
      );
    });

    if (conversationExists) {
      // Update existing conversation
      this.queryClient.setQueriesData(
        { queryKey: ['dm-conversations'], exact: false },
        (oldData: ConversationsData | undefined) => {
          if (!oldData?.pages) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              conversations: page.conversations.map((conv) => {
                if (conv.contact.id === conversationId) {
                  return {
                    ...conv,
                    lastMessage: {
                      ...conv.lastMessage,
                      body: metadata.lastMessage,
                      waTimestamp: metadata.lastMessageTime,
                    },
                    unreadCount: metadata.unreadCount,
                    messageCount: conv.messageCount + 1,
                  };
                }
                return conv;
              }),
            })),
          };
        },
      );
    } else {
      // New conversation - invalidate to refetch
      void this.queryClient.invalidateQueries({
        queryKey: ['dm-conversations'],
        exact: false,
      });
    }
  }

  private updateGroupMetadata(
    groupId: string,
    metadata: {
      lastMessage: string;
      lastMessageTime: string;
    },
  ) {
    if (!this.queryClient) return;

    interface Group {
      id: string;
      lastMessage: {
        body: string | null;
        waTimestamp: string | null;
        [key: string]: unknown;
      } | null;
      lastActivity: string | null;
      unreadCount: number;
      [key: string]: unknown;
    }

    interface GroupsInfiniteData {
      pages: {
        groups: Group[];
        [key: string]: unknown;
      }[];
      [key: string]: unknown;
    }

    const queries = this.queryClient.getQueriesData<GroupsInfiniteData>({
      queryKey: ['groups', 'infinite'],
      exact: false,
    });

    // Check if group exists in any query
    const groupExists = queries.some(([, data]) => {
      if (!data?.pages) return false;
      return data.pages.some((page) =>
        page.groups.some((group) => group.id === groupId),
      );
    });

    if (groupExists) {
      // Update existing group
      this.queryClient.setQueriesData(
        { queryKey: ['groups', 'infinite'], exact: false },
        (oldData: GroupsInfiniteData | undefined) => {
          if (!oldData?.pages) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              groups: page.groups.map((group) => {
                if (group.id === groupId) {
                  return {
                    ...group,
                    lastMessage: {
                      ...(group.lastMessage ?? {}),
                      body: metadata.lastMessage,
                      waTimestamp: metadata.lastMessageTime,
                    },
                    lastActivity: metadata.lastMessageTime,
                    unreadCount: group.unreadCount + 1,
                  };
                }
                return group;
              }),
            })),
          };
        },
      );
    } else {
      // New group - invalidate to refetch
      void this.queryClient.invalidateQueries({
        queryKey: ['groups', 'infinite'],
        exact: false,
      });
    }
  }

  private async handleSystemSync(data: SystemSyncData) {
    if (!this.queryClient) return;

    for (const event of data.events) {
      await this.handleV2Message(event);
    }
  }

  // Sequential sync with progress tracking
  private async syncWorkspaceData() {
    const steps: ('groups' | 'contacts' | 'groupData')[] = [
      'groups',
      'contacts',
      'groupData',
    ];
    const completedSteps: ('groups' | 'contacts' | 'groupData')[] = [];

    try {
      console.log('[WebSocket] 🔄 Starting sequential workspace sync...');

      for (const step of steps) {
        // Notify progress: starting this step
        this.notifySyncProgress({
          currentStep: step,
          completedSteps: [...completedSteps],
          totalSteps: steps.length,
          isComplete: false,
        });

        try {
          switch (step) {
            case 'groups':
              console.log(
                '[WebSocket] 📥 Step 1/3: Fetching available groups...',
              );
              await groupManagementApi.getAvailableGroups();
              console.log('[WebSocket] ✅ Available groups fetched');
              break;

            case 'contacts':
              console.log('[WebSocket] 📥 Step 2/3: Syncing contacts...');
              await contactsApi.syncContacts();
              console.log('[WebSocket] ✅ Contacts synced');
              break;

            case 'groupData':
              console.log('[WebSocket] 📥 Step 3/3: Syncing group data...');
              await groupsApi.syncGroups();
              console.log('[WebSocket] ✅ Group data synced');
              break;
          }

          // Mark step as completed
          completedSteps.push(step);
        } catch (err: unknown) {
          console.error(`[WebSocket] ❌ Failed to sync ${step}:`, err);
          // Continue with next step even if one fails
        }
      }

      // Notify completion
      this.notifySyncProgress({
        currentStep: 'groupData',
        completedSteps,
        totalSteps: steps.length,
        isComplete: true,
      });

      console.log('[WebSocket] ✅ Workspace sync completed');
    } finally {
      // Always reset sync flag, even if sync fails
      this.isSyncing = false;
    }
  }

  private notifySyncProgress(progress: {
    currentStep: 'groups' | 'contacts' | 'groupData';
    completedSteps: ('groups' | 'contacts' | 'groupData')[];
    totalSteps: number;
    isComplete: boolean;
  }): void {
    this.syncProgressListeners.forEach((listener) => {
      try {
        listener(progress);
      } catch (error) {
        console.error('Error in sync progress listener:', error);
      }
    });
  }

  private handleSessionStatus(data: SessionStatusData) {
    if (!this.queryClient) return;

    console.log('[WebSocket] 📡 session.status event received:', data);

    // Get the previous status before updating
    const previousData = this.queryClient.getQueryData(['waha-sessions']) as {
      session?: { status?: string };
    } | null;
    const previousStatus = previousData?.session?.status;

    // Update WAHA session query cache
    // Note: The query stores only response.data, not the full response
    this.queryClient.setQueryData(['waha-sessions'], (oldData: unknown) => {
      if (!oldData || typeof oldData !== 'object') {
        console.log('[WebSocket] ⚠️ No existing cache data for waha-sessions');
        return oldData;
      }

      const typedData = oldData as {
        session: {
          status: string;
          phoneNumber?: string | null;
          lastConnectedAt?: string | null;
          lastDisconnectedAt?: string | null;
          [key: string]: unknown;
        } | null;
        [key: string]: unknown;
      };

      if (!typedData.session) {
        console.log('[WebSocket] ⚠️ No session in cache data');
        return oldData;
      }

      const updatedData = {
        ...typedData,
        session: {
          ...typedData.session,
          status: data.status,
          phoneNumber: data.phoneNumber ?? typedData.session.phoneNumber,
          lastConnectedAt:
            data.lastConnectedAt ?? typedData.session.lastConnectedAt,
          lastDisconnectedAt:
            data.lastDisconnectedAt ?? typedData.session.lastDisconnectedAt,
        },
      };

      console.log('[WebSocket] ✅ Updated session cache:', updatedData);
      return updatedData;
    });

    // If status changed to WORKING, trigger sync
    // This handles: user scans QR, reconnects, or first-time setup
    if (data.status === 'WORKING' && previousStatus !== 'WORKING') {
      console.log('[WebSocket] 🔄 Session became WORKING, triggering sync...');
      this.triggerSync(); // Uses deduplication - won't sync if already syncing
    }
  }

  private handleSessionDeletionQueued(data: SessionDeletionQueuedData) {
    if (!this.queryClient) return;

    console.log('[WebSocket] 🗑️ session.deletion.queued:', data);

    this.queryClient.setQueryData(queryKeys.wahaSessions.deletion(data.jobId), {
      jobId: data.jobId,
      status: 'queued' as const,
    });
  }

  private handleSessionDeletionProgress(data: SessionDeletionProgressData) {
    if (!this.queryClient) return;

    console.log('[WebSocket] 📊 session.deletion.progress:', data);

    this.queryClient.setQueryData(queryKeys.wahaSessions.deletion(data.jobId), {
      jobId: data.jobId,
      status: 'active' as const,
      progress: {
        step: data.step,
        completedSteps: data.completedSteps,
        totalSteps: data.totalSteps,
        currentStep: data.currentStep,
      },
    });
  }

  private handleSessionDeletionComplete(data: SessionDeletionCompleteData) {
    if (!this.queryClient) return;

    console.log('[WebSocket] ✅ session.deletion.complete:', data);

    this.queryClient.setQueryData(queryKeys.wahaSessions.deletion(data.jobId), {
      jobId: data.jobId,
      status: 'completed' as const,
      completedAt: data.deletedAt,
    });

    // Invalidate session queries to refetch
    void this.queryClient.invalidateQueries({
      queryKey: queryKeys.wahaSessions.all,
    });
  }

  private handleSessionDeletionFailed(data: SessionDeletionFailedData) {
    if (!this.queryClient) return;

    console.log('[WebSocket] ❌ session.deletion.failed:', data);

    this.queryClient.setQueryData(queryKeys.wahaSessions.deletion(data.jobId), {
      jobId: data.jobId,
      status: 'failed' as const,
      error: data.error,
    });
  }

  private updateDMMessageInCache(
    conversationId: string,
    messageIdOrPredicate: string | ((msg: DMMessage) => boolean),
    updater: (msg: DMMessage) => DMMessage | null,
  ): boolean {
    if (!this.queryClient) return false;

    let updated = false;
    const isStringMatch = typeof messageIdOrPredicate === 'string';
    const limit = 20; // Default limit from useInfiniteDMMessages hook

    this.queryClient.setQueriesData(
      { queryKey: ['dm-messages', 'infinite', conversationId, limit] },
      (oldData: MessagesInfiniteData | undefined) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages
              .map((msg) => {
                const shouldUpdate = isStringMatch
                  ? msg.id === messageIdOrPredicate
                  : messageIdOrPredicate(msg);

                // Check for ID match

                if (shouldUpdate) {
                  // Update the message
                  const updatedMsg = updater(msg);
                  if (updatedMsg) {
                    updated = true;
                    return updatedMsg;
                  }
                }
                return msg;
              })
              .filter(Boolean), // Remove null messages (for deletion)
          })),
        };
      },
    );

    return updated;
  }

  private updateGroupMessageInCache(
    conversationId: string,
    messageIdOrPredicate: string | ((msg: GroupMessage) => boolean),
    updater: (msg: GroupMessage) => GroupMessage | null,
  ): boolean {
    if (!this.queryClient) return false;

    let updated = false;
    const isStringMatch = typeof messageIdOrPredicate === 'string';
    const limit = 20; // Default limit from useInfiniteMessages hook

    this.queryClient.setQueriesData(
      { queryKey: ['group-messages', 'infinite', conversationId, limit] },
      (oldData: GroupMessagesInfiniteData | undefined) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages
              .map((msg) => {
                const shouldUpdate = isStringMatch
                  ? msg.id === messageIdOrPredicate
                  : messageIdOrPredicate(msg);

                if (shouldUpdate) {
                  const updatedMsg = updater(msg);
                  if (updatedMsg) {
                    updated = true;
                    return updatedMsg;
                  }
                }
                return msg;
              })
              .filter(Boolean), // Remove null messages (for deletion)
          })),
        };
      },
    );

    return updated;
  }

  private addDMMessageToCache(conversationId: string, message: DMMessage) {
    if (!this.queryClient) return;

    const limit = 20; // Default limit from useInfiniteDMMessages hook

    this.queryClient.setQueriesData(
      { queryKey: ['dm-messages', 'infinite', conversationId, limit] },
      (oldData: MessagesInfiniteData | undefined) => {
        // If no cache exists yet, initialize it with the new message
        // AND restore all other queued messages for this conversation
        if (!oldData) {
          console.log(
            '[WebSocket] 🔄 Cache empty - restoring queued messages for conversation:',
            conversationId,
          );

          // Get all queued messages for this conversation from optimisticMessages
          const queuedMessages: DMMessage[] = [];
          this.optimisticMessages.forEach((optMsg) => {
            // Check if this optimistic message belongs to this DM conversation
            if (
              'dmContactId' in optMsg &&
              optMsg.dmContactId === conversationId
            ) {
              queuedMessages.push(optMsg as DMMessage);
            }
          });

          // Check if the new message is already in queued messages
          const messageExists = queuedMessages.some((m) => m.id === message.id);

          // Combine queued messages + new message (if not duplicate)
          const allMessages = messageExists
            ? queuedMessages
            : [...queuedMessages, message];

          return {
            pages: [
              {
                messages: allMessages,
                nextPage: null,
                hasMore: false,
              },
            ],
            pageParams: [1],
          };
        }

        const lastPageIndex = oldData.pages.length - 1;
        // If no pages exist, create the first page with the message
        if (lastPageIndex < 0) {
          return {
            ...oldData,
            pages: [
              {
                messages: [message],
                nextPage: null,
                hasMore: false,
              },
            ],
          };
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page, index) =>
            index === lastPageIndex
              ? {
                  ...page,
                  messages: [...page.messages, message],
                }
              : page,
          ),
        };
      },
    );

    // Force invalidation to trigger re-render
    void this.queryClient.invalidateQueries({
      queryKey: ['dm-messages', 'infinite', conversationId],
      exact: false,
      refetchType: 'none',
    });
  }

  private addGroupMessageToCache(
    conversationId: string,
    message: GroupMessage,
  ) {
    if (!this.queryClient) return;

    const limit = 20; // Default limit from useInfiniteMessages hook

    this.queryClient.setQueriesData(
      { queryKey: ['group-messages', 'infinite', conversationId, limit] },
      (oldData: GroupMessagesInfiniteData | undefined) => {
        // If no cache exists yet, initialize it with the new message
        // AND restore all other queued messages for this conversation
        if (!oldData) {
          // Get all queued messages for this group from optimisticMessages
          const queuedMessages: GroupMessage[] = [];
          this.optimisticMessages.forEach((optMsg, msgId) => {
            // Check if this optimistic message belongs to this group conversation
            // Group messages don't have dmContactId, so check by exclusion
            if (
              !('dmContactId' in optMsg) &&
              'reactions' in optMsg && // GroupMessage has reactions object
              message.id !== msgId // Don't include the message we're about to add
            ) {
              // We need to check if this message belongs to this specific group
              // Since we don't store groupId directly, we'll need to be careful
              // For now, restore all group messages (safer approach)
              queuedMessages.push(optMsg as GroupMessage);
            }
          });

          // Check if the new message is already in queued messages
          const messageExists = queuedMessages.some((m) => m.id === message.id);

          // Combine queued messages + new message (if not duplicate)
          const allMessages = messageExists
            ? queuedMessages
            : [...queuedMessages, message];

          return {
            pages: [
              {
                messages: allMessages,
                nextPage: null,
                hasMore: false,
              },
            ],
            pageParams: [1],
          };
        }

        const lastPageIndex = oldData.pages.length - 1;
        // If no pages exist, create the first page with the message
        if (lastPageIndex < 0) {
          return {
            ...oldData,
            pages: [
              {
                messages: [message],
                nextPage: null,
                hasMore: false,
              },
            ],
          };
        }

        const updated = {
          ...oldData,
          pages: oldData.pages.map((page, index) =>
            index === lastPageIndex
              ? {
                  ...page,
                  messages: [...page.messages, message],
                }
              : page,
          ),
        };

        return updated;
      },
    );

    // Force invalidation to trigger re-render
    void this.queryClient.invalidateQueries({
      queryKey: ['group-messages', 'infinite', conversationId],
      exact: false,
      refetchType: 'none',
    });
  }

  private isConversationOpen(
    conversationId: string,
    type: 'dm' | 'group',
  ): boolean {
    const limit = 20;
    const queryKey =
      type === 'dm'
        ? ['dm-messages', 'infinite', conversationId, limit]
        : ['group-messages', 'infinite', conversationId, limit];

    const queries = this.queryClient?.getQueryCache().findAll({
      queryKey,
      type: 'active',
    });

    return (queries?.length ?? 0) > 0;
  }

  private async fetchMessage(
    messageId: string,
    noCache = false,
  ): Promise<AnyMessage | null> {
    // Skip cache if noCache is true
    if (!noCache) {
      const cached = this.messageCache.get(messageId);
      if (cached) {
        return cached;
      }
    }

    try {
      // Use the unified endpoint that works for both DM and group messages
      const response = (await contactsApi.getDMMessage(messageId, noCache)) as {
        success?: boolean;
        data?: BackendMessageResponse;
      };

      if (response.success && response.data) {
        const data = response.data;

        // Map to the appropriate frontend type based on conversationType
        if (data.conversationType === 'dm') {
          const dmMessage: DMMessage = {
            id: data.id,
            wahaMessageId: data.id,
            dmContactId: data.conversationId,
            body: data.body,
            messageType: data.messageType,
            waTimestamp: data.waTimestamp,
            createdAt: data.createdAt,
            fromMe: data.fromMe,
            senderId: data.senderId,
            senderDisplayName: data.senderDisplayName,
            status: data.status,
            quotedMessageId: data.quotedMessageId,
            quotedMessage: data.quotedMessage as DMMessage['quotedMessage'],
            media: data.media.map((m) => ({
              id: m.id,
              s3Key: m.s3Key ?? '',
              mediaType: m.mediaType,
              originalFilename: m.originalFilename,
              mimeType: m.mimeType ?? '',
              sizeBytes: m.sizeBytes ?? 0,
              uploadStatus: m.uploadStatus ?? 'completed',
              aiDescription: m.aiDescription ?? null,
              presignedUrl: m.presignedUrl,
            })),
            reactions: data.reactions,
          } as DMMessage;

          this.messageCache.set(messageId, dmMessage);
          return dmMessage;
        } else {
          const groupMessage: GroupMessage = {
            id: data.id,
            wahaMessageId: data.id,
            threadId: null,
            senderId: data.senderId,
            senderDisplayName: data.senderDisplayName,
            waTimestamp: data.waTimestamp,
            messageType: data.messageType,
            body: data.body,
            quotedMessageId: data.quotedMessageId,
            quotedMessage: data.quotedMessage
              ? {
                  id: data.quotedMessage.id,
                  body: data.quotedMessage.body,
                  senderDisplayName: data.quotedMessage.senderDisplayName,
                  senderId: data.quotedMessage.senderId,
                  messageType: data.quotedMessage.messageType,
                  waTimestamp: data.quotedMessage.waTimestamp,
                  fromMe: data.quotedMessage.fromMe,
                  media: data.quotedMessage.media?.map((m) => ({
                    id: m.id,
                    s3Key: m.s3Key,
                    mediaType: m.mediaType,
                    originalFilename: m.originalFilename,
                    mimeType: m.mimeType,
                    sizeBytes: m.sizeBytes,
                    uploadStatus: m.uploadStatus,
                    aiDescription: m.aiDescription ?? null,
                    tags: m.tags ?? null,
                    presignedUrl: m.presignedUrl,
                  })),
                }
              : undefined,
            status: data.status,
            threadName: null,
            fromMe: data.fromMe,
            lastEditedAt: data.lastEditedAt
              ? new Date(data.lastEditedAt)
              : null,
            editCount: data.editCount,
            editHistory: null,
            createdAt: data.createdAt,
            media: data.media.map((m) => ({
              id: m.id,
              s3Key: m.s3Key,
              mediaType: m.mediaType,
              originalFilename: m.originalFilename,
              mimeType: m.mimeType,
              sizeBytes: m.sizeBytes,
              uploadStatus: m.uploadStatus,
              aiDescription: m.aiDescription ?? null,
              tags: m.tags ?? null,
              presignedUrl: m.presignedUrl,
            })),
            reactions: data.reactions,
          } as GroupMessage;

          this.messageCache.set(messageId, groupMessage);
          return groupMessage;
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  private async fetchDMMessage(
    messageId: string,
    noCache = false,
  ): Promise<DMMessage | null> {
    const message = await this.fetchMessage(messageId, noCache);
    // Check if it's a DM message
    if (message && 'dmContactId' in message) {
      return message as DMMessage;
    }
    return null;
  }

  private async fetchGroupMessage(
    messageId: string,
    noCache = false,
  ): Promise<GroupMessage | null> {
    const message = await this.fetchMessage(messageId, noCache);
    // Check if it's a group message
    if (message && !('dmContactId' in message)) {
      return message as GroupMessage;
    }
    return null;
  }

  // Batch fetch messages for efficiency
  public async fetchMessages(messageIds: string[]): Promise<AnyMessage[]> {
    const messages: AnyMessage[] = [];
    const idsToFetch: string[] = [];

    // Check cache first
    for (const id of messageIds) {
      const cached = this.messageCache.get(id);
      if (cached) {
        messages.push(cached);
      } else {
        idsToFetch.push(id);
      }
    }

    // Fetch missing messages
    if (idsToFetch.length > 0) {
      try {
        const response = (await contactsApi.batchGetDMMessages(idsToFetch)) as {
          success?: boolean;
          data?: BackendMessageResponse[];
        };

        if (response.success && response.data) {
          for (const data of response.data) {
            let message: AnyMessage | null = null;

            if (data.conversationType === 'dm') {
              message = {
                id: data.id,
                wahaMessageId: data.id,
                dmContactId: data.conversationId,
                body: data.body,
                messageType: data.messageType,
                waTimestamp: data.waTimestamp,
                createdAt: data.createdAt,
                fromMe: data.fromMe,
                senderId: data.senderId,
                senderDisplayName: data.senderDisplayName,
                status: data.status,
                quotedMessageId: data.quotedMessageId,
                quotedMessage: data.quotedMessage as DMMessage['quotedMessage'],
                media: data.media.map((m) => ({
                  id: m.id,
                  s3Key: m.s3Key ?? '',
                  mediaType: m.mediaType,
                  originalFilename: m.originalFilename,
                  mimeType: m.mimeType ?? '',
                  sizeBytes: m.sizeBytes ?? 0,
                  uploadStatus: m.uploadStatus ?? 'completed',
                  aiDescription: m.aiDescription ?? null,
                  presignedUrl: m.presignedUrl,
                })),
                reactions: data.reactions,
              } as DMMessage;
            } else {
              message = {
                id: data.id,
                wahaMessageId: data.id,
                threadId: null,
                senderId: data.senderId,
                senderDisplayName: data.senderDisplayName,
                waTimestamp: data.waTimestamp,
                messageType: data.messageType,
                body: data.body,
                quotedMessageId: data.quotedMessageId,
                quotedMessage: data.quotedMessage
                  ? {
                      id: data.quotedMessage.id,
                      body: data.quotedMessage.body,
                      senderDisplayName: data.quotedMessage.senderDisplayName,
                      senderId: data.quotedMessage.senderId,
                      messageType: data.quotedMessage.messageType,
                      waTimestamp: data.quotedMessage.waTimestamp,
                      fromMe: data.quotedMessage.fromMe,
                      media: data.quotedMessage.media?.map((m) => ({
                        id: m.id,
                        s3Key: m.s3Key,
                        mediaType: m.mediaType,
                        originalFilename: m.originalFilename,
                        mimeType: m.mimeType,
                        sizeBytes: m.sizeBytes,
                        uploadStatus: m.uploadStatus,
                        aiDescription: m.aiDescription ?? null,
                        tags: m.tags ?? null,
                        presignedUrl: m.presignedUrl,
                      })),
                    }
                  : undefined,
                status: data.status,
                threadName: null,
                fromMe: data.fromMe,
                lastEditedAt: data.lastEditedAt
                  ? new Date(data.lastEditedAt)
                  : null,
                editCount: data.editCount,
                editHistory: null,
                createdAt: data.createdAt,
                media: data.media.map((m) => ({
                  id: m.id,
                  s3Key: m.s3Key,
                  mediaType: m.mediaType,
                  originalFilename: m.originalFilename,
                  mimeType: m.mimeType,
                  sizeBytes: m.sizeBytes,
                  uploadStatus: m.uploadStatus,
                  aiDescription: m.aiDescription ?? null,
                  tags: m.tags ?? null,
                  presignedUrl: m.presignedUrl,
                })),
                reactions: data.reactions,
              } as GroupMessage;
            }

            this.messageCache.set(message.id, message);
            messages.push(message);
          }
        }
      } catch {
        // Ignore batch fetch errors
      }
    }

    return messages;
  }

  public clearCache() {
    this.messageCache.clear();
    this.optimisticMessages.clear();
  }

  // Public method to disconnect
  public disconnect() {
    this.isIntentionallyClosed = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }

    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.clearCache();
  }

  // Get connection status
  public isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // Get cached message
  public getCachedMessage(messageId: string): AnyMessage | undefined {
    return this.messageCache.get(messageId);
  }

  // Subscribe to connection status changes
  public subscribeToConnectionStatus(
    listener: (isConnected: boolean) => void,
  ): () => void {
    this.connectionListeners.add(listener);
    // Immediately notify the listener of current status
    listener(this.isConnected());

    // Return unsubscribe function
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  // Notify all connection listeners
  private notifyConnectionListeners(isConnected: boolean): void {
    this.connectionListeners.forEach((listener) => {
      try {
        listener(isConnected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  // Subscribe to sync progress updates
  public subscribeToSyncProgress(
    listener: (progress: {
      currentStep: 'groups' | 'contacts' | 'groupData';
      completedSteps: ('groups' | 'contacts' | 'groupData')[];
      totalSteps: number;
      isComplete: boolean;
    }) => void,
  ): () => void {
    this.syncProgressListeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.syncProgressListeners.delete(listener);
    };
  }

  // Manually trigger workspace sync (e.g., after login or session → WORKING)
  public triggerSync(): void {
    // Check if already syncing
    if (this.isSyncing) {
      return;
    }

    this.isSyncing = true;
    void this.syncWorkspaceData();
  }
}

// Export singleton instance
export const webSocketService = WebSocketService.getInstance();
